import React, { useMemo } from "react";
import ReactSelect from "react-select";
import ArrowDown from "@/shared/assets/images/ArrowDown.svg";
import type { CountryConfig } from "./country-list";

export type CountryCodeInputProps = {
  countryList: CountryConfig[];
  value: CountryConfig["code"];
  onChange: (value: CountryConfig["code"]) => void;
  error?: boolean;
};

type Option = { value: CountryConfig["code"]; label: string; country: CountryConfig };

export const CountryCodeInput: React.FC<CountryCodeInputProps> = ({
  countryList,
  value,
  onChange,
}) => {
  const options: Option[] = useMemo(
    () =>
      countryList.map((c) => ({
        value: c.code,
        label: `${c.flag} ${c.code}`,
        country: c,
      })),
    [countryList]
  );

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  return (
    <ReactSelect<Option, false>
      value={selectedOption}
      onChange={(option) => option && onChange(option.value)}
      options={options}
      placeholder="Код"
      isClearable={false}
      classNames={{
        control: () =>
          "w-full !outline-0 !shadow-none !min-h-[auto] !border-0 !border-none !p-0 !outline-none !flex !flex-row !items-center",
        valueContainer: () =>
          "!border-0 !outline-0 !p-0 text-color-dark text-lg !flex !flex-row !flex-1 !items-center !min-w-0 !overflow-hidden",
        placeholder: () => "text-color-dark/70 text-base !m-0 text-color-dark",
        menu: () => "!left-[0] !min-w-[270px]",
        input: () => "!m-0 !p-0",
        menuPortal: () => "!z-[9999]",
      }}
      styles={{
        valueContainer: (base) => ({
          ...base,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "nowrap",
        }),
        singleValue: (base) => ({
          ...base,
          position: "relative",
          zIndex: 1,
          gridArea: "unset",
        }),
        input: (base) => ({
          ...base,
          position: "relative",
          zIndex: 0,
          gridArea: "unset",
          flex: "1 1 2ch",
          minWidth: "2ch",
        }),
      }}
      classNamePrefix="react_select_custom"
      noOptionsMessage={() => "Нет опций"}
      menuPosition="fixed"
      menuPortalTarget={document.body}
      components={{
        DropdownIndicator: () => <ArrowDown height={20} width={20} />,
        IndicatorSeparator: () => null,
        SingleValue: ({ data }) => (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {data.country.flag} {data.value}
          </span>
        ),
      }}
      formatOptionLabel={(option) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {option.country.flag} {option.value} — {option.country.name}
        </span>
      )}
    />
  );
};
